import { useCallback, useEffect, useRef, useState } from "react";

const DRAFT_DEBOUNCE_MS = 500;
const CLEARED_RESET_MS = 2000;

function readDraft(storageKey, enabled) {
  if (!enabled) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // A malformed or unreadable entry is treated as no draft.
    return null;
  }
}

/**
 * Persists an in-progress new-note document to localStorage so the text
 * survives a navigation, a refresh, or an explicit close of the composer.
 *
 * @param {string} storageKey - Per-composer key, e.g. `note-draft:v1:Contact:42::AuditLog:9`.
 * @param {boolean} enabled - False for the edit case, which makes the hook inert.
 * @returns {{ initialDraft: object|null, status: "idle"|"saving"|"saved"|"cleared", saveDraft: (serialize: () => object) => void, clearDraft: () => void }}
 *   `initialDraft` is the stored editor state read once on mount. `status`
 *   reports the draft lifecycle for an on-screen cue. `saveDraft` takes a
 *   serializer rather than a document, and calls it inside the debounce, so a
 *   burst of keystrokes serializes the document once rather than once each.
 */
export function useNoteDraft(storageKey, enabled) {
  const [initialDraft] = useState(() => readDraft(storageKey, enabled));
  // A restored draft is already saved, so the returning user sees the cue at once.
  const [status, setStatus] = useState(() => (initialDraft ? "saved" : "idle"));
  const timeoutRef = useRef(null);
  // Whether a draft currently exists, so clearDraft only reports a removal the
  // user can perceive and not the empty first change that fires on mount.
  const hasDraftRef = useRef(!!initialDraft);

  const cancelPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const saveDraft = useCallback(
    serialize => {
      if (!enabled) return;
      cancelPending();
      setStatus("saving");
      hasDraftRef.current = true;
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        try {
          localStorage.setItem(storageKey, JSON.stringify(serialize()));
          setStatus("saved");
        } catch {
          // A quota or serialization failure leaves the draft unsaved; the
          // composer text is still present until the composer unmounts. The
          // status is not advanced to "saved" for a write that did not land.
        }
      }, DRAFT_DEBOUNCE_MS);
    },
    [enabled, storageKey, cancelPending]
  );

  const clearDraft = useCallback(() => {
    if (!enabled) return;
    cancelPending();
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Nothing to recover from; the key simply remains.
    }
    if (!hasDraftRef.current) return;
    hasDraftRef.current = false;
    setStatus("cleared");
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setStatus("idle");
    }, CLEARED_RESET_MS);
  }, [enabled, storageKey, cancelPending]);

  useEffect(() => cancelPending, [cancelPending]);

  return { initialDraft, status, saveDraft, clearDraft };
}
